import {
  LaserConverter,
  RemoveBackgroundFull,
  RemoveBackgroundOutsideImage,
  RemoveColor,
  ReplaceColor,
  UserActionData,
} from '@/web-components/import-wizard/typings';
import { defineStore } from 'pinia';
import {
  CropInput,
  RemoveBackgroundFullInput,
  RemoveBackgroundOutsideImageInput,
  RemoveColorInput,
  ReplaceColorInput,
  UserActionInputType,
  UserAction,
  PmsColor,
} from '@midocean/webapps-shared-domain';

export const useStore = defineStore({
  id: 'actions',

  state: () => {
    return {
      sequenceNum: 0,
      actions: [] as UserActionData[],
    };
  },

  actions: {
    createRemoveColorAction(shapeId: string, colorToRemove: PmsColor) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'RemoveColor',
          colorToRemove: colorToRemove,
        },
      };
      this.pushNewAction(actionPayload);
    },
    createReplaceColorAction(shapeId: string, from: PmsColor, to: PmsColor) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'ReplaceColor',
          colorToReplace: from,
          replaceWith: to,
        },
      };
      this.pushNewAction(actionPayload);
    },
    createRemoveBackgroundFullAction(
      shapeId: string,
      backgroundColor: PmsColor
    ) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'RemoveBackgroundFull',
          backgroundColor: backgroundColor,
        },
      };
      this.pushNewAction(actionPayload);
    },
    createRemoveBackgroundOutsideImageAction(
      shapeId: string,
      backgroundColor: PmsColor
    ) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'RemoveBackgroundOutsideImage',
          backgroundColor: backgroundColor,
        },
      };
      this.pushNewAction(actionPayload);
    },
    createLaserConverterAction(shapeId: string) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'LaserConverter',
        },
      };
      this.pushNewAction(actionPayload);
    },
    createCropAction(
      shapeId: string,
      crop: { left: number; right: number; top: number; bottom: number }
    ) {
      this.sequenceNum = this.sequenceNum + 1;
      const actionPayload = {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'Crop',
          croppedFromLeftPct: crop.left,
          croppedFromRightPct: crop.right,
          croppedFromTopPct: crop.top,
          croppedFromBottomPct: crop.bottom,
        },
      };
      this.pushNewAction(actionPayload);
    },
    pushNewAction(action: UserActionData) {
      this.actions.push({ ...action });
    },
    prepareActionsPayload(shapeId: string): UserAction[] {
      const shapeUserActions: UserActionData[] = this.actions
        .filter((action) => action.shapeId === shapeId)
        .sort((a, b) => a.seq - b.seq);

      const mapUserActions: UserAction[] = shapeUserActions.map(
        (action, index) => {
          return {
            userActionType: action.action.$type,
            sequenceNumber: index + 1,
            userActionInput: {
              ...action.action,
              $type: UserActionInputType[action.action.$type],
            },
          } as UserAction;
        }
      );
      return mapUserActions;
    },

    initializeUserActionForArtwork(shapeId: string, actions: UserAction[]) {
      actions.map((act) => {
        if (act.userActionType === 'LaserConverter') {
          this.sequenceNum = this.sequenceNum + 1;
          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              $type: 'LaserConverter',
            } as LaserConverter,
          };

          this.pushNewAction(action);
          return;
        }

        if (act.userActionType === 'ReplaceColor') {
          this.sequenceNum = this.sequenceNum + 1;
          const replaceClrPayload = act.userActionInput as ReplaceColorInput;

          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              $type: 'ReplaceColor',
              colorToReplace: replaceClrPayload.colorToReplace,
              replaceWith: replaceClrPayload.replaceWith,
            } as ReplaceColor,
          };

          this.pushNewAction(action);
          return;
        }

        if (act.userActionType === 'RemoveColor') {
          this.sequenceNum = this.sequenceNum + 1;
          const removeClrPayload = act.userActionInput as RemoveColorInput;

          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              $type: 'RemoveColor',
              colorToRemove: removeClrPayload.colorToRemove,
            } as RemoveColor,
          };

          this.pushNewAction(action);
          return;
        }

        if (act.userActionType === 'RemoveBackgroundOutsideImage') {
          this.sequenceNum = this.sequenceNum + 1;
          const removeBackClcOutsidePayload =
            act.userActionInput as RemoveBackgroundOutsideImageInput;

          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              $type: 'RemoveBackgroundOutsideImage',
              backgroundColor: removeBackClcOutsidePayload.backgroundColor,
            } as RemoveBackgroundOutsideImage,
          };

          this.pushNewAction(action);
          return;
        }

        if (act.userActionType === 'RemoveBackgroundFull') {
          this.sequenceNum = this.sequenceNum + 1;
          const removeBackClcFullPayload =
            act.userActionInput as RemoveBackgroundFullInput;

          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              $type: 'RemoveBackgroundFull',
              backgroundColor: removeBackClcFullPayload.backgroundColor,
            } as RemoveBackgroundFull,
          };

          this.pushNewAction(action);
          return;
        }

        if (act.userActionType === 'Crop') {
          this.sequenceNum = this.sequenceNum + 1;
          const cropPayload = act.userActionInput as CropInput;

          const action: UserActionData = {
            seq: this.sequenceNum,
            shapeId: shapeId,
            action: {
              ...cropPayload,
              $type: 'Crop',
            } as CropInput,
          };

          this.pushNewAction(action);
          return;
        }
      });
    },
    createDebossingConverterAction(shapeId: string) {
      this.sequenceNum = this.sequenceNum + 1;
      return {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'DebossingConverter',
        },
      };
    },
    createSublimationConverterAction(shapeId: string) {
      this.sequenceNum = this.sequenceNum + 1;
      return {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'SublimationConverter',
        },
      };
    },
    createPantoneConverterAction(shapeId: string) {
      this.sequenceNum = this.sequenceNum + 1;
      return {
        seq: this.sequenceNum,
        shapeId: shapeId,
        action: {
          $type: 'PantoneConverter',
        },
      };
    },
  },
});
